import { Link } from '@tanstack/react-router';
import { Icon } from '../Icon/Icon';
import { formatAmount, formatDate } from '../../utils/accountTransform';
import styles from './Payment.module.scss';

export type PaymentType = 'incoming' | 'outgoing';

export interface PaymentProps {
  type: PaymentType;
  title: string;
  date: Date | string;
  transactionNumber: string;
  amount: number;
  currency: string;
  onClick?: () => void;
  href?: string;
  'data-testid': string;
}

export const Payment = ({
  type,
  title,
  date,
  transactionNumber,
  amount,
  currency,
  onClick,
  href,
  'data-testid': testId,
}: PaymentProps) => {
  const isOutgoing = type === 'outgoing';
  const arrowIcon = isOutgoing ? 'arrow-left' : 'arrow-right';
  const formattedAmount = formatAmount(amount);
  const formattedDate = formatDate(date);
  const subtitle = `${formattedDate} · ${transactionNumber}`;

  const className = [
    styles['payment'],
    onClick || href ? styles['payment--interactive'] : '',
  ]
    .filter(Boolean)
    .join(' ');

  const ariaLabel = `${title}, ${subtitle}, ${formattedAmount} ${currency}`;

  const content = (
    <>
      <div className={styles['payment-left']}>
        <div
          className={[
            styles['icon-wrapper'],
            isOutgoing
              ? styles['icon-wrapper--outgoing']
              : styles['icon-wrapper--incoming'],
          ]
            .filter(Boolean)
            .join(' ')}
          aria-hidden="true"
        >
          <Icon name={arrowIcon} size={24} />
        </div>
        <div className={styles['payment-info']}>
          <div className={styles['payment-title']}>{title}</div>
          <div className={styles['payment-subtitle']}>{subtitle}</div>
        </div>
      </div>
      <div
        className={[
          styles['payment-amount'],
          isOutgoing ? styles['payment-amount--outgoing'] : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {isOutgoing && '-'}
        {formattedAmount} {currency}
      </div>
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className={className}
        data-testid={testId}
        aria-label={ariaLabel}
        onClick={onClick}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        {content}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        data-testid={testId}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={className} data-testid={testId} aria-label={ariaLabel}>
      {content}
    </div>
  );
};
